import * as SelectPrimitive from '@radix-ui/react-select';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import * as React from 'react';

import { cn } from '@apps/web/lib/utils';

/*
 * Admin control set — the uniform input/select/toggle/button family.
 *
 * PHO-14461 Part C: Phoebe purple is the signature colour. Primary
 * CTAs, focus rings, toggle on-state, and the live dot all resolve
 * to --admin-purple; secondary/ghost/danger keep their neutral rest
 * states so surfaces stay quiet. Radii are locked to 4px
 * (buttons/inputs/toggle track excluded), transitions enumerate exact
 * properties — never `all` — so paint work is scoped, and the depress
 * micro splits into a 60ms depress + 120ms return so the press reads
 * as a real physical action rather than a linear scale.
 */

const adminButtonVariants = cva(
    // Base: hairline defaults, tabular-nums so numeric labels (counts,
    // shortcuts) don't shift; scoped transitions so paint stays tight.
    // The 40px hit-area extension is provided by a `::before` overlay
    // in admin_theme.css keyed on `data-hit-area="extend"` — opt in
    // via the `hitArea` prop below on isolated compact controls only.
    // Adjacent sm/iconSm buttons at the common 8px gap would overlap
    // 6px if the pseudo were applied by default.
    "admin-button relative inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-admin-sm font-medium whitespace-nowrap tabular-nums transition-[color,background-color,border-color,transform] duration-[var(--admin-dur-fast)] ease-[var(--admin-ease-in-out)] disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--admin-purple)] [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:opacity-60 [&_svg]:transition-opacity [&_svg]:duration-[var(--admin-dur-fast)] [&_svg]:ease-[var(--admin-ease-in-out)] hover:[&_svg]:opacity-100 focus-visible:[&_svg]:opacity-100 [&_svg:not([class*='size-'])]:size-3.5",
    {
        variants: {
            variant: {
                // Primary CTA: Phoebe purple fill on white text.
                // Hover shifts to the strong purple tone; the split
                // press timing sells depth.
                primary:
                    'bg-admin-purple text-white hover:bg-admin-purple-strong',
                // Secondary: paper surface, hairline border. Hover
                // strengthens the border toward purple and adds a
                // hairline shadow so it reads as picked-up.
                secondary:
                    'border border-admin-border bg-admin-surface text-admin-ink hover:border-admin-purple-border hover:shadow-[var(--admin-shadow-hairline)]',
                // Ghost: transparent. Hover uses the ultra-light
                // purple wash so quiet actions still land under the
                // signature hue on interaction.
                ghost: 'text-admin-ink-muted hover:bg-admin-purple-wash hover:text-admin-ink',
                // Danger: text + border only at rest — a fill would
                // dominate a table row full of them. Hover adds the
                // ultra-light danger tint from the theme.
                danger: 'border border-admin-danger-border bg-transparent text-admin-danger-text hover:bg-admin-danger-surface',
            },
            size: {
                // sm: 26px height — fits into toolbar chip rails and
                // table row actions. Pairs with an invisible 40px
                // pseudo hit target from the theme layer.
                sm: 'text-admin-xs h-[26px] px-2',
                // md: 32px height, 13px body, 12px hpadding — the
                // default control rhythm across the kit.
                md: 'text-admin-base h-8 px-3',
                // icon: square variants match the md/sm heights so
                // icon+label toolbars align without ad-hoc padding.
                icon: 'size-8',
                iconSm: 'size-[26px]',
            },
        },
        defaultVariants: {
            variant: 'secondary',
            size: 'md',
        },
    },
);

export function AdminButton({
    className,
    variant,
    size,
    asChild = false,
    hitArea = false,
    type,
    ...props
}: React.ComponentProps<'button'> &
    VariantProps<typeof adminButtonVariants> & {
        readonly asChild?: boolean;
        /**
         * Opt in to the 40px hit-area extension for compact sizes
         * (sm/iconSm). Use only for isolated controls (48px+ padding
         * around); the pseudo-element would overlap sibling compact
         * controls at typical 8px gaps and steal their clicks.
         */
        readonly hitArea?: boolean;
    }) {
    const Comp = asChild ? Slot : 'button';
    const resolvedSize = size ?? 'md';
    return (
        <Comp
            data-slot="admin-button"
            data-size={resolvedSize}
            data-hit-area={hitArea ? 'extend' : undefined}
            // Buttons in admin toolbars live inside forms; never submit
            // implicitly.
            type={asChild ? type : (type ?? 'button')}
            className={cn(
                adminButtonVariants({ variant, size: resolvedSize }),
                className,
            )}
            {...props}
        />
    );
}

export function AdminInput({
    className,
    ...props
}: React.ComponentProps<'input'>) {
    // Focus ring (ink border + 1px ink inset shadow) is applied by the
    // admin scope in admin_theme.css so every raw <input> under the
    // admin theme reads consistently; do NOT add a duplicate ring class
    // here or the two will visually stack.
    return (
        <input
            data-slot="admin-input"
            className={cn(
                'rounded-admin-sm border-admin-border bg-admin-surface text-admin-base text-admin-ink placeholder:text-admin-ink-faint h-8 w-full border px-2.5 transition-[border-color] duration-[var(--admin-dur-fast)] ease-[var(--admin-ease-in-out)] disabled:cursor-not-allowed disabled:opacity-50',
                className,
            )}
            {...props}
        />
    );
}

export function AdminSearchInput({
    className,
    containerClassName,
    onClear,
    ...props
}: React.ComponentProps<'input'> & {
    readonly containerClassName?: string;
    /**
     * Optional callback fired when the clear button is clicked. If
     * omitted, the input still shows the clear button whenever the
     * `value` prop is non-empty and calls `onChange` with `''`.
     */
    readonly onClear?: () => void;
}) {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const hasValue = typeof props.value === 'string' && props.value.length > 0;
    const clear = () => {
        if (onClear !== undefined) {
            onClear();
        } else if (props.onChange !== undefined) {
            // Synthesize a minimal ChangeEvent so uncontrolled callers
            // relying on onChange still see the value drop.
            const target =
                inputRef.current ??
                ({ value: '' } as unknown as HTMLInputElement);
            target.value = '';
            props.onChange({
                target,
                currentTarget: target,
            } as unknown as React.ChangeEvent<HTMLInputElement>);
        }
        inputRef.current?.focus();
    };
    return (
        <div
            data-slot="admin-search-input"
            className={cn('relative', containerClassName)}
        >
            <Search
                aria-hidden="true"
                data-testid="admin-search-icon"
                strokeWidth={1.75}
                className="text-admin-ink-muted pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2"
            />
            <AdminInput
                ref={inputRef}
                type="search"
                className={cn('pl-8', hasValue && 'pr-8', className)}
                {...props}
            />
            {hasValue && (
                <button
                    type="button"
                    aria-label="Clear search"
                    data-testid="admin-search-clear"
                    onClick={clear}
                    className="text-admin-ink-muted hover:text-admin-ink absolute top-1/2 right-2 flex size-5 -translate-y-1/2 cursor-pointer items-center justify-center rounded-sm transition-[color] duration-[var(--admin-dur-fast)] ease-[var(--admin-ease-in-out)] focus-visible:ring-2 focus-visible:ring-[color:var(--admin-purple)] focus-visible:outline-none focus-visible:ring-inset"
                >
                    <X aria-hidden="true" strokeWidth={2} className="size-3" />
                </button>
            )}
        </div>
    );
}

export type AdminSelectOption = {
    readonly value: string;
    readonly label: React.ReactNode;
    readonly disabled?: boolean;
};

export function AdminSelect({
    value,
    onValueChange,
    options,
    placeholder,
    className,
    containerClassName,
    disabled,
    'aria-label': ariaLabel,
}: {
    readonly value?: string;
    readonly onValueChange?: (value: string) => void;
    readonly options: ReadonlyArray<AdminSelectOption>;
    readonly placeholder?: React.ReactNode;
    readonly className?: string;
    readonly containerClassName?: string;
    readonly disabled?: boolean;
    readonly 'aria-label'?: string;
}) {
    // Radix Select ships arrow-key + type-ahead navigation, portalled
    // popover, and controlled/uncontrolled variants — the round-4
    // contract needs the popover motion + focus-ring specifics, so we
    // wrap the primitives instead of a native <select>.
    return (
        <div
            data-slot="admin-select"
            className={cn('relative w-fit', containerClassName)}
        >
            <SelectPrimitive.Root
                value={value}
                onValueChange={onValueChange}
                disabled={disabled}
            >
                <SelectPrimitive.Trigger
                    aria-label={ariaLabel}
                    data-slot="admin-select-trigger"
                    className={cn(
                        'admin-select-trigger rounded-admin-sm border-admin-border bg-admin-surface text-admin-base text-admin-ink data-[placeholder]:text-admin-ink-faint inline-flex h-8 cursor-pointer items-center justify-between gap-2 border pr-2 pl-2.5 transition-[border-color] duration-[var(--admin-dur-fast)] ease-[var(--admin-ease-in-out)] focus-visible:ring-2 focus-visible:ring-[color:var(--admin-purple)] focus-visible:outline-none focus-visible:ring-inset disabled:cursor-not-allowed disabled:opacity-50',
                        className,
                    )}
                >
                    <SelectPrimitive.Value placeholder={placeholder} />
                    <SelectPrimitive.Icon asChild={true}>
                        <ChevronDown
                            aria-hidden="true"
                            strokeWidth={1.75}
                            className="text-admin-ink-muted size-3"
                        />
                    </SelectPrimitive.Icon>
                </SelectPrimitive.Trigger>
                <SelectPrimitive.Portal>
                    <SelectPrimitive.Content
                        // 180ms opacity + translateY enter tuned to the
                        // Linear default. Radix drives `data-state`
                        // open/closed transitions; the theme layer owns
                        // the keyframes so the timing stays consistent.
                        position="popper"
                        sideOffset={4}
                        data-slot="admin-select-content"
                        className="admin-select-content admin-theme border-admin-border bg-admin-surface rounded-admin-md z-50 min-w-[8rem] overflow-hidden border shadow-[var(--admin-shadow-elevated)]"
                    >
                        <SelectPrimitive.Viewport className="p-1">
                            {options.map((option) => (
                                <SelectPrimitive.Item
                                    key={option.value}
                                    value={option.value}
                                    disabled={option.disabled}
                                    data-slot="admin-select-item"
                                    className="text-admin-base text-admin-ink data-[highlighted]:bg-admin-purple-wash rounded-admin-sm relative flex h-7 cursor-pointer items-center pr-2 pl-6 outline-none data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50"
                                >
                                    <span className="absolute left-1 flex size-4 items-center justify-center">
                                        <SelectPrimitive.ItemIndicator>
                                            <Check
                                                strokeWidth={2}
                                                className="text-admin-ink size-3"
                                            />
                                        </SelectPrimitive.ItemIndicator>
                                    </span>
                                    <SelectPrimitive.ItemText>
                                        {option.label}
                                    </SelectPrimitive.ItemText>
                                </SelectPrimitive.Item>
                            ))}
                        </SelectPrimitive.Viewport>
                    </SelectPrimitive.Content>
                </SelectPrimitive.Portal>
            </SelectPrimitive.Root>
        </div>
    );
}

export function AdminToggle({
    checked,
    onCheckedChange,
    disabled = false,
    className,
    ...props
}: Omit<React.ComponentProps<'button'>, 'onClick' | 'children'> & {
    readonly checked: boolean;
    readonly onCheckedChange: (checked: boolean) => void;
    readonly disabled?: boolean;
}) {
    // The visible track is small (26×16 pill); the outer button carries
    // the ~40px hit area so the toggle stays comfortably tappable and
    // shows a focus ring on the whole target, not just the pill.
    // Track uses Phoebe purple when on and border-strong when off, so
    // the on/off contrast reads high and reinforces the brand hue.
    return (
        <button
            data-slot="admin-toggle"
            type="button"
            role="switch"
            aria-checked={checked}
            disabled={disabled}
            onClick={() => onCheckedChange(!checked)}
            className={cn(
                'rounded-admin-sm focus-visible:ring-admin-purple flex h-8 w-10 cursor-pointer items-center justify-center focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset disabled:cursor-not-allowed disabled:opacity-50',
                className,
            )}
            {...props}
        >
            <span
                aria-hidden="true"
                data-testid="admin-toggle-track"
                className={cn(
                    'flex h-4 w-[26px] items-center rounded-full px-0.5 transition-[background-color] duration-[var(--admin-dur-base)] ease-[var(--admin-ease-in-out)]',
                    checked ? 'bg-admin-purple' : 'bg-admin-border-strong',
                )}
            >
                <span
                    className={cn(
                        'bg-admin-surface size-3 rounded-full shadow-[var(--admin-shadow-hairline)] transition-transform duration-[var(--admin-dur-base)] ease-[var(--admin-ease-in-out)]',
                        checked && 'translate-x-2.5',
                    )}
                />
            </span>
        </button>
    );
}
